"use client";

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { PwaInstallContext, type PwaInstallOutcome } from "@/contexts/pwaInstallContext";
import { isPwaStandalone } from "@/lib/isPwaStandalone";
import {
  PWA_INSTALL_DISMISSAL_MS,
  PWA_INSTALL_DISMISSAL_STORAGE_KEY,
} from "@/lib/pwaInstallStorage";
import { readPwaInstallDismissedUntil } from "@/lib/readPwaInstallDismissedUntil";
import { writePwaInstallDismissedUntil } from "@/lib/writePwaInstallDismissedUntil";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export function PwaInstallProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (isPwaStandalone()) return;

    function handleBeforeInstallPrompt(event: Event) {
      event.preventDefault();
      if (readPwaInstallDismissedUntil() > Date.now()) return;
      setInstallEvent(event as BeforeInstallPromptEvent);
    }

    function handleAppInstalled() {
      setInstallEvent(null);
      try {
        window.localStorage.removeItem(PWA_INSTALL_DISMISSAL_STORAGE_KEY);
      } catch {
        // The installed state is authoritative even when storage is unavailable.
      }
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const dismiss = useCallback((_surface: string) => {
    writePwaInstallDismissedUntil(Date.now() + PWA_INSTALL_DISMISSAL_MS);
    setInstallEvent(null);
  }, []);

  const prompt = useCallback(
    async (_surface: string): Promise<PwaInstallOutcome> => {
      if (!installEvent) return "unavailable";

      try {
        await installEvent.prompt();
        const choice = await installEvent.userChoice;
        setInstallEvent(null);
        if (choice.outcome === "dismissed") {
          writePwaInstallDismissedUntil(Date.now() + PWA_INSTALL_DISMISSAL_MS);
        }
        return choice.outcome;
      } catch {
        setInstallEvent(null);
        return "error";
      }
    },
    [installEvent],
  );

  const value = useMemo(
    () => ({ canInstall: Boolean(installEvent), dismiss, prompt }),
    [dismiss, installEvent, prompt],
  );

  return <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>;
}
